import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Separator } from '../../components/ui/separator';
import { Textarea } from '../../components/ui/textarea';
import { Slider } from '../../components/ui/slider';
import { 
  Bot, Settings, Users, Activity, Phone, PhoneIncoming, Shield, 
  Brain, Zap, RefreshCw, Save, Eye, Play, Clock, CheckCircle, 
  AlertCircle, XCircle, ChevronRight, ChevronLeft, MessageSquare,
  Target, Calendar, Headphones, TrendingUp, BarChart2, Sparkles,
  Lock, Unlock, AlertTriangle, Edit2, Trash2, Plus, Search, Filter
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Specialty configurations
const SPECIALTY_CONFIG = {
  general_inbound: { label: 'General Inbound', color: 'bg-blue-500', icon: Phone },
  discovery_screening: { label: 'Discovery Screening', color: 'bg-purple-500', icon: Target },
  appointment_scheduling: { label: 'Scheduling', color: 'bg-green-500', icon: Calendar },
  sales_demo: { label: 'Sales Demo', color: 'bg-yellow-500', icon: TrendingUp },
  operations_support: { label: 'Ops Support', color: 'bg-orange-500', icon: Headphones },
  overflow_backup: { label: 'Overflow', color: 'bg-gray-500', icon: Users },
};

// Agent status badges
const StatusBadge = ({ isEnabled, isAvailable, currentSessionId }) => {
  if (currentSessionId) {
    return (
      <Badge className="bg-green-500 text-white border-0">
        <Phone className="h-3 w-3 mr-1 animate-pulse" />
        On Call
      </Badge>
    );
  }
  if (!isEnabled) {
    return (
      <Badge variant="outline" className="text-red-500 border-red-500/30">
        <XCircle className="h-3 w-3 mr-1" />
        Disabled
      </Badge>
    );
  }
  if (!isAvailable) {
    return (
      <Badge variant="outline" className="text-yellow-500 border-yellow-500/30">
        <AlertCircle className="h-3 w-3 mr-1" />
        Busy
      </Badge>
    );
  }
  return (
    <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
      <CheckCircle className="h-3 w-3 mr-1" />
      Ready
    </Badge>
  );
};

// Single Agent Card Component
const AgentCard = ({ agent, isAdmin, onEdit, onToggle, isSelected }) => {
  const specialty = SPECIALTY_CONFIG[agent.specialty] || SPECIALTY_CONFIG.general_inbound;
  const SpecialtyIcon = specialty.icon;

  return (
    <Card 
      className={`cursor-pointer transition-all hover:shadow-md ${isSelected ? 'ring-2 ring-primary' : ''}`}
      onClick={() => onEdit(agent)}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full ${specialty.color} flex items-center justify-center`}>
              <Bot className="h-5 w-5 text-white" />
            </div>
            <div>
              <h4 className="font-semibold text-sm">{agent.name}</h4>
              <p className="text-xs text-muted-foreground">&quot;{agent.display_name}&quot;</p>
            </div>
          </div>
          <StatusBadge 
            isEnabled={agent.is_enabled} 
            isAvailable={agent.is_available}
            currentSessionId={agent.current_session_id}
          />
        </div>

        <div className="flex items-center gap-2 mb-3">
          <Badge variant="outline" className={`${specialty.color}/20 border-0 text-xs`}>
            <SpecialtyIcon className="h-3 w-3 mr-1" />
            {specialty.label}
          </Badge>
          {agent.sessions_today > 0 && (
            <Badge variant="outline" className="text-xs">
              {agent.sessions_today} today
            </Badge>
          )}
        </div>

        <div className="flex items-center justify-between">
          <div className="text-xs text-muted-foreground">
            Total: {agent.total_sessions || 0} sessions
          </div>
          {isAdmin && (
            <Switch
              checked={agent.is_enabled}
              onCheckedChange={(checked) => {
                onToggle(agent.id, checked);
              }}
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Agent Edit Panel Component
const AgentEditPanel = ({ agent, isAdmin, onSave, onClose, onTest }) => {
  const [formData, setFormData] = useState({
    display_name: agent?.display_name || '',
    specialty: agent?.specialty || 'general_inbound',
    specialty_weight: agent?.specialty_weight || 0.7,
    system_prompt: agent?.system_prompt || '',
    greeting_prompt: agent?.greeting_prompt || '',
    screening_prompt: agent?.screening_prompt || '',
    guardrails: agent?.guardrails || [],
    max_hold_offer_seconds: agent?.max_hold_offer_seconds || 120,
    escalation_keywords: agent?.escalation_keywords || [],
    temperature: agent?.temperature || 0.7,
    max_tokens: agent?.max_tokens || 500,
    model_name: agent?.model_name || 'gpt-4o',
  });
  const [newGuardrail, setNewGuardrail] = useState('');
  const [newKeyword, setNewKeyword] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [testMessage, setTestMessage] = useState('');
  const [testResponse, setTestResponse] = useState(null);
  const [isTesting, setIsTesting] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(agent.id, formData);
      toast.success('Agent configuration saved');
    } catch (err) {
      toast.error('Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testMessage.trim()) {
      toast.error('Enter a test message');
      return;
    }
    setIsTesting(true);
    try {
      const response = await onTest(agent.id, testMessage);
      setTestResponse(response);
    } catch (err) {
      toast.error('Test failed');
    } finally {
      setIsTesting(false);
    }
  };

  const addGuardrail = () => {
    if (newGuardrail.trim()) {
      setFormData(prev => ({
        ...prev,
        guardrails: [...prev.guardrails, newGuardrail.trim()]
      }));
      setNewGuardrail('');
    }
  };

  const removeGuardrail = (index) => {
    setFormData(prev => ({
      ...prev,
      guardrails: prev.guardrails.filter((_, i) => i !== index)
    }));
  };

  const addKeyword = () => {
    if (newKeyword.trim()) {
      setFormData(prev => ({
        ...prev,
        escalation_keywords: [...prev.escalation_keywords, newKeyword.trim().toLowerCase()]
      }));
      setNewKeyword('');
    }
  };

  const removeKeyword = (index) => {
    setFormData(prev => ({
      ...prev,
      escalation_keywords: prev.escalation_keywords.filter((_, i) => i !== index)
    }));
  };

  if (!agent) return null;

  const specialty = SPECIALTY_CONFIG[agent.specialty];

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full ${specialty?.color || 'bg-gray-500'} flex items-center justify-center`}>
            <Bot className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold">{agent.name}</h3>
            <p className="text-sm text-muted-foreground">Configure prompts and behavior</p>
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      {/* Content */}
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-6">
          {/* Identity Section */}
          <div className="space-y-4">
            <h4 className="font-medium flex items-center gap-2">
              <Users className="h-4 w-4" />
              Identity
            </h4>
            
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label>Display Name (what caller hears)</Label>
                <Input
                  value={formData.display_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                  placeholder="Alex"
                  disabled={!isAdmin}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Specialty Bias</Label>
                <Select 
                  value={formData.specialty}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, specialty: value }))}
                  disabled={!isAdmin}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(SPECIALTY_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        {config.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Specialty is a routing bias, not a hard restriction
                </p>
              </div>

              <div className="space-y-2">
                <Label>Specialty Weight: {(formData.specialty_weight * 100).toFixed(0)}%</Label>
                <Slider
                  value={[formData.specialty_weight * 100]}
                  onValueChange={([value]) => setFormData(prev => ({ ...prev, specialty_weight: value / 100 }))}
                  min={0}
                  max={100}
                  step={5}
                  disabled={!isAdmin}
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Prompts Section */}
          <div className="space-y-4">
            <h4 className="font-medium flex items-center gap-2">
              <Brain className="h-4 w-4" />
              Prompts
            </h4>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label>System Prompt</Label>
                <Textarea
                  value={formData.system_prompt}
                  onChange={(e) => setFormData(prev => ({ ...prev, system_prompt: e.target.value }))}
                  placeholder="You are a professional AI call handler..."
                  rows={6}
                  disabled={!isAdmin}
                />
                <p className="text-xs text-muted-foreground">
                  Main instructions for the AI&apos;s behavior and role
                </p>
              </div>

              <div className="space-y-2">
                <Label>Greeting Prompt</Label>
                <Textarea
                  value={formData.greeting_prompt}
                  onChange={(e) => setFormData(prev => ({ ...prev, greeting_prompt: e.target.value }))}
                  placeholder="Thank you for calling..."
                  rows={3}
                  disabled={!isAdmin}
                />
                <p className="text-xs text-muted-foreground">
                  What the AI says when answering a call
                </p>
              </div>

              <div className="space-y-2">
                <Label>Screening Prompt</Label>
                <Textarea
                  value={formData.screening_prompt}
                  onChange={(e) => setFormData(prev => ({ ...prev, screening_prompt: e.target.value }))}
                  placeholder="Based on the caller's response, determine their intent..."
                  rows={3}
                  disabled={!isAdmin}
                />
                <p className="text-xs text-muted-foreground">
                  How the AI determines caller intent
                </p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Guardrails Section */}
          <div className="space-y-4">
            <h4 className="font-medium flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Guardrails
            </h4>

            <div className="space-y-2">
              {formData.guardrails.map((rule, index) => (
                <div key={index} className="flex items-center gap-2 p-2 bg-muted/50 rounded">
                  <AlertTriangle className="h-4 w-4 text-yellow-500 flex-shrink-0" />
                  <span className="text-sm flex-1">{rule}</span>
                  {isAdmin && (
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-6 w-6"
                      onClick={() => removeGuardrail(index)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              ))}
              
              {isAdmin && (
                <div className="flex gap-2">
                  <Input
                    value={newGuardrail}
                    onChange={(e) => setNewGuardrail(e.target.value)}
                    placeholder="Add a safety rule..."
                    onKeyPress={(e) => e.key === 'Enter' && addGuardrail()}
                  />
                  <Button size="icon" onClick={addGuardrail}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>

          <Separator />

          {/* Escalation Keywords */}
          <div className="space-y-4">
            <h4 className="font-medium flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Escalation Keywords
            </h4>
            <p className="text-xs text-muted-foreground">
              If caller says these words, escalate immediately to human
            </p>

            <div className="flex flex-wrap gap-2">
              {formData.escalation_keywords.map((keyword, index) => (
                <Badge key={index} variant="secondary" className="gap-1">
                  {keyword}
                  {isAdmin && (
                    <button onClick={() => removeKeyword(index)} className="ml-1 hover:text-destructive">
                      <XCircle className="h-3 w-3" />
                    </button>
                  )}
                </Badge>
              ))}
            </div>

            {isAdmin && (
              <div className="flex gap-2">
                <Input
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  placeholder="Add keyword..."
                  onKeyPress={(e) => e.key === 'Enter' && addKeyword()}
                />
                <Button size="icon" onClick={addKeyword}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          <Separator />

          {/* Model Settings */}
          <div className="space-y-4">
            <h4 className="font-medium flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Model Settings
            </h4>

            <div className="grid gap-4">
              <div className="space-y-2">
                <Label>Temperature: {formData.temperature}</Label>
                <Slider
                  value={[formData.temperature * 100]}
                  onValueChange={([value]) => setFormData(prev => ({ ...prev, temperature: value / 100 }))}
                  min={0}
                  max={100}
                  step={5}
                  disabled={!isAdmin}
                />
                <p className="text-xs text-muted-foreground">
                  Higher = more creative, Lower = more consistent
                </p>
              </div>

              <div className="space-y-2">
                <Label>Max Hold Offer (seconds)</Label>
                <Input
                  type="number"
                  value={formData.max_hold_offer_seconds}
                  onChange={(e) => setFormData(prev => ({ ...prev, max_hold_offer_seconds: parseInt(e.target.value) || 120 }))}
                  disabled={!isAdmin}
                />
                <p className="text-xs text-muted-foreground">
                  How long to offer holding for a human agent
                </p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Test Section */}
          <div className="space-y-4">
            <h4 className="font-medium flex items-center gap-2">
              <Play className="h-4 w-4" />
              Test Agent
            </h4>

            <div className="space-y-2">
              <Textarea
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                placeholder="Enter a test message from a caller..."
                rows={2}
              />
              <Button 
                onClick={handleTest} 
                disabled={isTesting}
                className="w-full"
              >
                {isTesting ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Testing...
                  </>
                ) : (
                  <>
                    <Zap className="h-4 w-4 mr-2" />
                    Test Response
                  </>
                )}
              </Button>
            </div>

            {testResponse && (
              <Card className="bg-muted/50">
                <CardContent className="p-3">
                  <div className="text-sm space-y-2">
                    <p className="font-medium">Response:</p>
                    <p className="text-muted-foreground">{testResponse.response}</p>
                    <div className="flex gap-2 mt-2">
                      <Badge variant="outline">Intent: {testResponse.intent_detected}</Badge>
                      <Badge variant="outline">Action: {testResponse.suggested_action}</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </ScrollArea>

      {/* Footer */}
      {isAdmin && (
        <div className="p-4 border-t">
          <Button onClick={handleSave} disabled={isSaving} className="w-full">
            {isSaving ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Configuration
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

// Routing Config Panel
const RoutingConfigPanel = ({ config, isAdmin, onSave }) => {
  const [formData, setFormData] = useState({
    ai_overflow_enabled: config?.ai_overflow_enabled ?? true,
    human_first: config?.human_first ?? true,
    max_human_wait_seconds: config?.max_human_wait_seconds ?? 30,
    routing_strategy: config?.routing_strategy ?? 'round_robin',
    ai_active_outside_hours_only: config?.ai_active_outside_hours_only ?? false,
    max_concurrent_ai_sessions: config?.max_concurrent_ai_sessions ?? 10,
  });
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(formData);
      toast.success('Routing configuration saved');
    } catch (err) {
      toast.error('Failed to save routing configuration');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Routing Configuration
        </CardTitle>
        <CardDescription>
          Configure how inbound calls are routed between human and AI agents
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Main Toggle */}
        <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
          <div className="space-y-1">
            <div className="font-medium flex items-center gap-2">
              <Bot className="h-4 w-4" />
              AI Overflow Enabled
            </div>
            <p className="text-sm text-muted-foreground">
              Route calls to AI agents when no humans are available
            </p>
          </div>
          <Switch
            checked={formData.ai_overflow_enabled}
            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, ai_overflow_enabled: checked }))}
            disabled={!isAdmin}
          />
        </div>

        {/* Human First Toggle */}
        <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
          <div className="space-y-1">
            <div className="font-medium flex items-center gap-2">
              <Users className="h-4 w-4" />
              Human First
            </div>
            <p className="text-sm text-muted-foreground">
              Always try to route to human agents before AI
            </p>
          </div>
          <Switch
            checked={formData.human_first}
            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, human_first: checked }))}
            disabled={!isAdmin}
          />
        </div>

        {/* Wait Time */}
        <div className="space-y-2">
          <Label>Max Wait for Human (seconds)</Label>
          <Input
            type="number"
            value={formData.max_human_wait_seconds}
            onChange={(e) => setFormData(prev => ({ ...prev, max_human_wait_seconds: parseInt(e.target.value) || 30 }))}
            disabled={!isAdmin}
          />
          <p className="text-xs text-muted-foreground">
            Time to wait for human agent before routing to AI
          </p>
        </div>

        {/* Routing Strategy */}
        <div className="space-y-2">
          <Label>Routing Strategy</Label>
          <Select
            value={formData.routing_strategy}
            onValueChange={(value) => setFormData(prev => ({ ...prev, routing_strategy: value }))}
            disabled={!isAdmin}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="round_robin">Round Robin</SelectItem>
              <SelectItem value="specialty_weighted">Specialty Weighted</SelectItem>
              <SelectItem value="least_busy">Least Busy</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Max Concurrent Sessions */}
        <div className="space-y-2">
          <Label>Max Concurrent AI Sessions</Label>
          <Input
            type="number"
            value={formData.max_concurrent_ai_sessions}
            onChange={(e) => setFormData(prev => ({ ...prev, max_concurrent_ai_sessions: parseInt(e.target.value) || 10 }))}
            disabled={!isAdmin}
          />
        </div>

        {isAdmin && (
          <Button onClick={handleSave} disabled={isSaving} className="w-full">
            {isSaving ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Routing Config
              </>
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

// Main Component
export default function AIAnswerAgentsSettings() {
  const { user } = useAuth();
  const [agents, setAgents] = useState([]);
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('agents');
  const [filterSpecialty, setFilterSpecialty] = useState('all');

  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [agentsRes, statsRes, configRes] = await Promise.all([
        axios.get(`${API}/call-center/ai-answer/agents`, { headers }),
        axios.get(`${API}/call-center/ai-answer/stats`, { headers }),
        axios.get(`${API}/call-center/ai-answer/config`, { headers }),
      ]);

      setAgents(agentsRes.data.agents || []);
      setStats(statsRes.data);
      setConfig(configRes.data);
    } catch (err) {
      console.error('Failed to fetch AI Answer data:', err);
      toast.error('Failed to load AI Answer Agents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggleAgent = async (agentId, enabled) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.patch(
        `${API}/call-center/ai-answer/agents/${agentId}/status?enabled=${enabled}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAgents(prev => prev.map(a => 
        a.id === agentId ? { ...a, is_enabled: enabled } : a
      ));
      
      toast.success(`Agent ${enabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error('Failed to update agent status');
    }
  };

  const handleSaveAgent = async (agentId, data) => {
    const token = localStorage.getItem('token');
    await axios.patch(
      `${API}/call-center/ai-answer/agents/${agentId}`,
      data,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    // Refresh agent data
    const response = await axios.get(
      `${API}/call-center/ai-answer/agents/${agentId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    setAgents(prev => prev.map(a => 
      a.id === agentId ? response.data : a
    ));
    setSelectedAgent(response.data);
  };

  const handleTestAgent = async (agentId, message) => {
    const token = localStorage.getItem('token');
    const response = await axios.post(
      `${API}/call-center/ai-answer/agents/${agentId}/test`,
      { test_message: message },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  };

  const handleSaveConfig = async (data) => {
    const token = localStorage.getItem('token');
    await axios.patch(
      `${API}/call-center/ai-answer/config`,
      data,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setConfig(prev => ({ ...prev, ...data }));
  };

  const filteredAgents = filterSpecialty === 'all' 
    ? agents 
    : agents.filter(a => a.specialty === filterSpecialty);

  if (!isAdmin) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[80vh]">
          <Card className="max-w-md">
            <CardContent className="p-6 text-center">
              <Lock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Admin Access Required</h2>
              <p className="text-muted-foreground">
                Only administrators can configure AI Answer Agents.
              </p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="h-[calc(100vh-4rem)] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Bot className="h-6 w-6" />
                AI Answer Agents
              </h1>
              <p className="text-muted-foreground">
                Configure and train AI agents for overflow call handling
              </p>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Stats Summary */}
              {stats && (
                <div className="flex items-center gap-6 text-sm">
                  <div className="text-center">
                    <div className="font-semibold text-lg">{stats.agents?.enabled || 0}</div>
                    <div className="text-muted-foreground text-xs">Enabled</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-lg text-green-500">{stats.agents?.available || 0}</div>
                    <div className="text-muted-foreground text-xs">Available</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-lg text-blue-500">{stats.sessions?.today_total || 0}</div>
                    <div className="text-muted-foreground text-xs">Today</div>
                  </div>
                </div>
              )}
              
              <Button variant="outline" onClick={fetchData}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-4">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="agents" className="gap-2">
                  <Bot className="h-4 w-4" />
                  Agents ({agents.length})
                </TabsTrigger>
                <TabsTrigger value="routing" className="gap-2">
                  <Settings className="h-4 w-4" />
                  Routing Config
                </TabsTrigger>
                <TabsTrigger value="sessions" className="gap-2">
                  <Activity className="h-4 w-4" />
                  Sessions
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {activeTab === 'agents' && (
            <div className="flex h-full">
              {/* Agent List */}
              <div className={`${selectedAgent ? 'w-1/2 border-r' : 'w-full'} p-4 overflow-auto`}>
                {/* Filter */}
                <div className="flex items-center gap-2 mb-4">
                  <Filter className="h-4 w-4 text-muted-foreground" />
                  <Select value={filterSpecialty} onValueChange={setFilterSpecialty}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Filter by specialty" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Specialties</SelectItem>
                      {Object.entries(SPECIALTY_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>
                          {config.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Badge variant="outline">{filteredAgents.length} agents</Badge>
                </div>

                {/* Agent Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredAgents.map(agent => (
                    <AgentCard
                      key={agent.id}
                      agent={agent}
                      isAdmin={isAdmin}
                      onEdit={setSelectedAgent}
                      onToggle={handleToggleAgent}
                      isSelected={selectedAgent?.id === agent.id}
                    />
                  ))}
                </div>

                {filteredAgents.length === 0 && (
                  <div className="text-center py-12">
                    <Bot className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No agents found</p>
                  </div>
                )}
              </div>

              {/* Edit Panel */}
              {selectedAgent && (
                <div className="w-1/2 border-l bg-background">
                  <AgentEditPanel
                    agent={selectedAgent}
                    isAdmin={isAdmin}
                    onSave={handleSaveAgent}
                    onClose={() => setSelectedAgent(null)}
                    onTest={handleTestAgent}
                  />
                </div>
              )}
            </div>
          )}

          {activeTab === 'routing' && (
            <div className="p-6 max-w-2xl mx-auto">
              <RoutingConfigPanel
                config={config}
                isAdmin={isAdmin}
                onSave={handleSaveConfig}
              />
            </div>
          )}

          {activeTab === 'sessions' && (
            <div className="p-6">
              <Card>
                <CardHeader>
                  <CardTitle>Recent AI Sessions</CardTitle>
                  <CardDescription>
                    View and review calls handled by AI agents
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-12 text-muted-foreground">
                    <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Session history will appear here as calls are handled</p>
                    <p className="text-sm mt-2">
                      Today: {stats?.sessions?.today_total || 0} sessions
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
